/**
 * Vehicle Wear Report
 * Körbeteende kopplat till fordonsslitage (Uppsala krav 5.5.1.3)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import DateRangeFilter from '@/components/common/DateRangeFilter';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import {
  WrenchScrewdriverIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { DataTable, ReportPageLayout, LoadingState, SummaryCard } from '@/components/common';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

interface WearCategory {
  category: string;
  categoryKey: string;
  score: number;
  events: number;
  description: string;
  recommendation: string;
}

interface VehicleWearStats {
  vehicleId: number;
  registrationNumber: string;
  make: string;
  model: string;
  organizationName: string;
  totalTrips: number;
  totalDistance: number;
  overallWearScore: number;
  wearCategories: WearCategory[];
}

interface WearSummary {
  totalVehicles: number;
  avgWearScore: number;
  worstCategory: string;
  vehiclesAtRisk: number;
}

const VehicleWearReport: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [summary, setSummary] = useState<WearSummary | null>(null);
  const [vehicles, setVehicles] = useState<VehicleWearStats[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleWearStats | null>(null);

  // Hämta slitage-data
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedOrg || !dateRange.startDate || !dateRange.endDate) return;

      setLoading(true);
      try {
        const response = await api.get('/api/reports/vehicle-wear', {
          params: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            organizationId: selectedOrg,
          },
        });

        if (response.data?.success) {
          setSummary(response.data.data.summary);
          setVehicles(response.data.data.vehicles);
        }
      } catch {
        // Error fetching vehicle wear data - handled silently
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedOrg, dateRange]);

  const getScoreColor = (score: number) => {
    if (score < 30) return 'text-green-600';
    if (score < 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score < 30) return 'bg-green-100';
    if (score < 70) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/reports/vehicle-wear/export', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          organizationId: selectedOrg,
          format: 'csv',
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vehicle_wear_${dateRange.startDate}_${dateRange.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // Export error - handled silently
    }
  };

  // Radardata för valt fordon
  const radarData = selectedVehicle?.wearCategories.map(cat => ({
    category: cat.category,
    score: cat.score,
    fullMark: 100,
  })) || [];

  return (
    <ReportPageLayout
      title={t('vehicleWear.title')}
      description={t('vehicleWear.description')}
      icon={<WrenchScrewdriverIcon className="w-6 h-6 text-primary" />}
      actions={
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:bg-bgSecondary"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          {t('common.export')}
        </button>
      }
      filters={
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">
              {t('common.organization')}
            </label>
            <OrgTreeSelect
              value={selectedOrg}
              onChange={setSelectedOrg}
              showEmptyOption={false}
            />
          </div>
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(field, value) =>
              setDateRange((prev) => ({ ...prev, [field]: value }))
            }
          />
        </div>
      }
    >
      {loading ? (
        <LoadingState />
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <SummaryCard
                title={t('vehicleWear.totalVehicles')}
                value={summary.totalVehicles}
              />
              <SummaryCard
                title={t('vehicleWear.avgWearScore')}
                value={`${summary.avgWearScore}/100`}
                valueClassName={getScoreColor(summary.avgWearScore)}
              />
              <SummaryCard
                title={t('vehicleWear.worstCategory')}
                value={summary.worstCategory}
                valueClassName="text-orange-600"
              />
              <SummaryCard
                title={t('vehicleWear.vehiclesAtRisk')}
                value={<><ExclamationTriangleIcon className="h-6 w-6" />{summary.vehiclesAtRisk}</>}
                valueClassName="text-red-600"
              />
            </div>
          )}

          {/* Charts and Table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart */}
            <div className="bg-bgCard rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {t('vehicleWear.wearByVehicle')}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vehicles.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="registrationNumber" type="category" width={80} />
                  <Tooltip />
                  <Bar
                    dataKey="overallWearScore"
                    name={t('vehicleWearReport.wearScore')}
                    fill="#f97316"
                    onClick={(data) => setSelectedVehicle(data)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Radar Chart for selected vehicle */}
            <div className="bg-bgCard rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {selectedVehicle
                  ? `${t('vehicleWear.wearProfile')}: ${selectedVehicle.registrationNumber}`
                  : t('vehicleWear.selectVehicle')}
              </h3>
              {selectedVehicle ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="category" />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} />
                    <Radar
                      name={t('vehicleWearReport.wear')}
                      dataKey="score"
                      stroke="#f97316"
                      fill="#f97316"
                      fillOpacity={0.6}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-textSecondary">
                  {t('vehicleWear.clickBarToSelect')}
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="bg-bgCard rounded-lg shadow-sm overflow-hidden">
            <DataTable<VehicleWearStats>
              data={vehicles}
              rowKey="vehicleId"
              pageSize={20}
              emptyMessage={t('vehicleWearReport.noVehicleData')}
              onRowClick={(row) => setSelectedVehicle(row)}
              columns={[
                { key: 'registrationNumber', label: t('common.vehicle'), sortable: true, csvValue: (_, row) => `${row.registrationNumber} ${row.make} ${row.model}`, render: (_, row) => (<><div className="font-medium text-textDefault">{row.registrationNumber}</div><div className="text-sm text-textSecondary">{row.make} {row.model}</div></>) },
                { key: 'totalDistance', label: t('common.distance'), align: 'right', sortable: true, render: (v) => (v as number).toLocaleString() + ' km' },
                { key: '_brakes', label: t('vehicleWear.brakes'), align: 'center', sortable: true, sortValue: (row) => row.wearCategories.find(c => c.categoryKey === 'brakes')?.score ?? 0, csvValue: (_, row) => String(row.wearCategories.find(c => c.categoryKey === 'brakes')?.score ?? 0), render: (_, row) => { const s = row.wearCategories.find(c => c.categoryKey === 'brakes')?.score ?? 0; return <span className={`px-2 py-1 rounded text-sm ${getScoreBg(s)} ${getScoreColor(s)}`}>{s}</span>; } },
                { key: '_engine', label: t('vehicleWear.engine'), align: 'center', sortable: true, sortValue: (row) => row.wearCategories.find(c => c.categoryKey === 'engine')?.score ?? 0, csvValue: (_, row) => String(row.wearCategories.find(c => c.categoryKey === 'engine')?.score ?? 0), render: (_, row) => { const s = row.wearCategories.find(c => c.categoryKey === 'engine')?.score ?? 0; return <span className={`px-2 py-1 rounded text-sm ${getScoreBg(s)} ${getScoreColor(s)}`}>{s}</span>; } },
                { key: '_tires', label: t('vehicleWear.tires'), align: 'center', sortable: true, sortValue: (row) => row.wearCategories.find(c => c.categoryKey === 'tires')?.score ?? 0, csvValue: (_, row) => String(row.wearCategories.find(c => c.categoryKey === 'tires')?.score ?? 0), render: (_, row) => { const s = row.wearCategories.find(c => c.categoryKey === 'tires')?.score ?? 0; return <span className={`px-2 py-1 rounded text-sm ${getScoreBg(s)} ${getScoreColor(s)}`}>{s}</span>; } },
                { key: '_fuel', label: t('vehicleWear.fuel'), align: 'center', sortable: true, sortValue: (row) => row.wearCategories.find(c => c.categoryKey === 'fuel')?.score ?? 0, csvValue: (_, row) => String(row.wearCategories.find(c => c.categoryKey === 'fuel')?.score ?? 0), render: (_, row) => { const s = row.wearCategories.find(c => c.categoryKey === 'fuel')?.score ?? 0; return <span className={`px-2 py-1 rounded text-sm ${getScoreBg(s)} ${getScoreColor(s)}`}>{s}</span>; } },
                { key: 'overallWearScore', label: t('vehicleWear.overall'), align: 'center', sortable: true, render: (v) => { const n = Number(v ?? 0); return <span className={`px-3 py-1 rounded-full font-bold ${getScoreBg(n)} ${getScoreColor(n)}`}>{n}</span>; } },
              ]}
              features={{ csvExport: true, search: true, stickyHeader: true }}
            />
          </div>

          {/* Selected Vehicle Details */}
          {selectedVehicle && (
            <div className="bg-bgCard rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {t('vehicleWear.recommendations')} {selectedVehicle.registrationNumber}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedVehicle.wearCategories.map((cat) => (
                  <div key={cat.categoryKey} className={`p-4 rounded-lg ${getScoreBg(cat.score)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{cat.category}</span>
                      <span className={`font-bold ${getScoreColor(cat.score)}`}>{cat.score}/100</span>
                    </div>
                    <p className="text-sm text-textSecondary mb-2">{cat.description}</p>
                    <div className="flex items-center gap-2 text-sm">
                      {cat.score < 30 ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
                      )}
                      <span>{cat.recommendation}</span>
                    </div>
                    <div className="text-xs text-textTertiary mt-2">
                      {cat.categoryKey === 'fuel' 
                        ? t('copy.reports.idleMinutes', { count: cat.events })
                        : t('copy.reports.eventCount', { count: cat.events })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </ReportPageLayout>
  );
};

export default VehicleWearReport;
